/**
 * Energy Summary + Targets — shared card consumed by Diet Recall & Meal Plan.
 *
 * Left panel: 3 NutrientPieChart donuts (Recall / Burns / Remaining).
 * Right panel: 5 TargetSummaryRows (Energy, Carbs, Protein, Fiber, Fats).
 *
 * In Diet Recall the Burns/Remaining donuts are dimmed (opacity-20) because
 * targets aren't being planned against — set `isMealPlan` to render them
 * full-opacity.
 */
import * as React from "react";

import { Widget, WidgetContent } from "@/components/consultation/shared/Widget";
import { cn } from "@/lib/utils";
import type { MealData } from "@/types/nutrition";

import { NutrientPieChart } from "./nutrition-charts";
import {
  NUTRITION_COLORS,
  type NutrientPieSegment,
} from "./nutrition-chart-config";
import {
  carbsGramsToKcal,
  fatsGramsToKcal,
  fiberGramsToKcal,
  getNutrientFromCategory,
  proteinsGramsToKcal,
} from "./meal-constants";

// =============================================================================
// TargetSummaryRow — one row in the Targets panel
// =============================================================================

interface TargetSummaryRowProps {
  title: string;
  value: number;
  idealValue?: number;
  percentage: number;
  unit: string;
  color: string;
  showIdeal?: boolean;
}

const TargetSummaryRow: React.FC<TargetSummaryRowProps> = ({
  title,
  value,
  idealValue,
  percentage,
  unit,
  color,
  showIdeal: _showIdeal = false,
}) => {
  const clampedPercentage = Math.min(100, Math.max(0, percentage || 0));
  const isExceeded = percentage > 100;
  const displayPercent =
    percentage === 0 || percentage === null ? null : percentage;

  // If the consumer didn't pass an explicit ideal, derive it from the % so the
  // "x / y unit" text always renders. Matches the Flutter formula.
  const calculatedIdeal =
    idealValue ?? (percentage > 0 ? (value * 100) / percentage : undefined);

  const formatIdealDisplay = () => {
    if (
      calculatedIdeal === undefined ||
      calculatedIdeal === Infinity ||
      isNaN(calculatedIdeal)
    ) {
      return "—";
    }
    return title === "Energy"
      ? Math.round(calculatedIdeal).toString()
      : calculatedIdeal.toFixed(1);
  };

  const formatValueDisplay = () => {
    if (value === 0 && calculatedIdeal === undefined) {
      return "—";
    }
    return title === "Energy" ? Math.round(value).toString() : value.toFixed(1);
  };

  return (
    <div className="flex items-center gap-3">
      <span className="z-body-md w-14 shrink-0">{title}</span>
      <div className="flex-1 space-y-1">
        <div className="flex items-center justify-between z-body">
          <span className="text-muted-foreground tabular-nums">
            {formatValueDisplay()} / {formatIdealDisplay()} {unit}
          </span>
          <span
            className={cn(
              "z-body-md tabular-nums",
              isExceeded && "text-destructive"
            )}
          >
            {displayPercent !== null ? `${displayPercent.toFixed(0)}%` : "0%"}
          </span>
        </div>
        <div
          className="relative h-2 w-full overflow-hidden rounded-full"
          style={{ backgroundColor: `${color}26` }}
        >
          <div
            className="h-full rounded-full transition-all"
            style={{
              width: `${clampedPercentage}%`,
              backgroundColor: color,
            }}
          />
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// EnergySummaryCard
// =============================================================================

interface EnergySummaryCardProps {
  totalNutrients: MealData["total_nutrients"] | null;
  isMealPlan?: boolean;
  energyTarget?: number;
  tdee?: number;
  bmr?: number;
  paCalories?: number;
  targets?: {
    energy?: number;
    carbs?: number;
    protein?: number;
    fiber?: number;
    fats?: number;
  };
}

export const EnergySummaryCard: React.FC<EnergySummaryCardProps> = ({
  totalNutrients,
  isMealPlan = false,
  energyTarget: _energyTarget,
  tdee = 0,
  bmr = 0,
  paCalories = 0,
  targets,
}) => {
  if (!totalNutrients) return null;

  const nutrients = totalNutrients as unknown as {
    general?: Record<
      string,
      { amount: number; unit: string; rda_percentage: number | null }
    >;
    carbohydrates?: Record<
      string,
      { amount: number; unit: string; rda_percentage: number | null }
    >;
    protein?: Record<
      string,
      { amount: number; unit: string; rda_percentage: number | null }
    >;
    lipids?: Record<
      string,
      { amount: number; unit: string; rda_percentage: number | null }
    >;
  };

  // These read `total_nutrients` by literal nutrient NAME, so a backend merge of
  // duplicate NutrientType rows can blank a value here with no error. Columbus
  // guards them: CONSOLE_NUTRIENT_KEYS in
  // nutrition/tests_merge_duplicate_nutrient_types.py mirrors these names and
  // fails CI if a merge retires one. Adding or changing a name below? Tell
  // backend to add it there — a key absent from that list is unprotected.
  const energyData = getNutrientFromCategory(nutrients.general, "Energy");
  const carbsData = getNutrientFromCategory(
    nutrients.carbohydrates,
    "Carbohydrate"
  );
  const proteinData = getNutrientFromCategory(nutrients.protein, "Protein");
  // Transitional: fibre is split across two nutrient types. Columbus migration
  // 0051 consolidates onto "Fiber"; until every environment has it, read the
  // canonical name first and fall back to the legacy one. Drop the fallback once
  // 0051 is applied to staging and production.
  const fiberData =
    getNutrientFromCategory(nutrients.carbohydrates, "Fiber") ??
    getNutrientFromCategory(nutrients.carbohydrates, "Fiber - Total");
  const fatData = getNutrientFromCategory(nutrients.lipids, "Fat");

  const energy = energyData?.amount || 0;
  const carbsGrams = carbsData?.amount || 0;
  const proteinGrams = proteinData?.amount || 0;
  const fiberGrams = fiberData?.amount || 0;
  const fatGrams = fatData?.amount || 0;

  // Pie centre / target row read the backend's `Energy.amount` directly
  // — same source the row kcal (`entry.calculated_energy`) and group
  // header sum to, so all four displayed totals agree.
  const totalKcal = energy;

  // Slice values are Atwater-derived for the breakdown visual. Meal Plan
  // uses net carbs (total − fiber, fiber priced separately at 2 kcal/g);
  // Diet Recall stays on gross carbs to match its prior behaviour.
  // The slice sum may diverge slightly from totalKcal (different
  // accounting from the food database), which is expected.
  const carbsForChart = isMealPlan
    ? Math.max(0, carbsGrams - fiberGrams)
    : carbsGrams;
  const carbsKcal = carbsGramsToKcal(carbsForChart);
  const proteinKcal = proteinsGramsToKcal(proteinGrams);
  const fiberKcal = fiberGramsToKcal(fiberGrams);
  const fatKcal = fatsGramsToKcal(fatGrams);

  const recallSegments: NutrientPieSegment[] = [
    { key: "carbs", value: carbsKcal },
    { key: "protein", value: proteinKcal },
    { key: "fiber", value: fiberKcal },
    { key: "fats", value: fatKcal },
  ];

  const burnsSegments: NutrientPieSegment[] = [
    { key: "bmr", value: bmr },
    { key: "activity", value: paCalories },
  ];

  // Burns/Burned chart always shows TDEE (BMR + PA Calories).
  // Meal Plan's Remaining chart is anchored to TDEE — the user-entered
  // calorie target lives in the Targets panel on the right and is not
  // the same axis. Diet Recall falls back to TDEE since it has no target.
  const burnsTotal = tdee;
  const remainingTarget = isMealPlan ? tdee : targets?.energy || tdee;
  const remaining = Math.max(0, remainingTarget - totalKcal);
  const remainingSegments: NutrientPieSegment[] = [
    { key: "remaining", value: remaining },
    { key: "consumed", value: totalKcal },
  ];

  const recallTitle = isMealPlan ? "Planned" : "Recall";
  const burnsTitle = isMealPlan ? "Burned" : "Burns";

  const getPercentage = (
    value: number,
    target?: number,
    rdaPercent?: number | null
  ) => {
    if (target && target > 0) {
      return (value / target) * 100;
    }
    return rdaPercent || 0;
  };

  const energyPercent = getPercentage(
    totalKcal,
    targets?.energy,
    energyData?.rda_percentage
  );
  const carbsPercent = getPercentage(
    carbsGrams,
    targets?.carbs,
    carbsData?.rda_percentage
  );
  const proteinPercent = getPercentage(
    proteinGrams,
    targets?.protein,
    proteinData?.rda_percentage
  );
  const fiberPercent = getPercentage(
    fiberGrams,
    targets?.fiber,
    fiberData?.rda_percentage
  );
  const fatsPercent = getPercentage(
    fatGrams,
    targets?.fats,
    fatData?.rda_percentage
  );

  return (
    <Widget>
      <WidgetContent className="py-4">
        <div className="flex gap-6">
          {/* Left: Energy Summary with Pie Charts */}
          <div className="flex-1">
            <h3 className="z-h3 text-foreground mb-3">Energy Summary</h3>
            <div className="flex items-start justify-around gap-4">
              <NutrientPieChart
                title={recallTitle}
                value={Math.round(totalKcal)}
                total={totalKcal}
                segments={recallSegments}
              />

              {burnsTotal > 0 && (
                <div className={cn(!isMealPlan && "opacity-20")}>
                  <NutrientPieChart
                    title={burnsTitle}
                    value={Math.round(burnsTotal)}
                    total={burnsTotal}
                    segments={burnsSegments}
                  />
                </div>
              )}

              {remainingTarget > 0 && (
                <div className={cn(!isMealPlan && "opacity-20")}>
                  <NutrientPieChart
                    title="Remaining"
                    value={Math.round(remaining)}
                    total={remainingTarget}
                    segments={remainingSegments}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Right: Targets Panel */}
          <div className="flex-1">
            <h3 className="z-h3 text-foreground mb-3">Targets</h3>
            <div className="space-y-2">
              <TargetSummaryRow
                title="Energy"
                value={totalKcal}
                idealValue={targets?.energy}
                percentage={energyPercent}
                unit="kcal"
                color={NUTRITION_COLORS.energy}
                showIdeal={!!targets?.energy}
              />
              <TargetSummaryRow
                title="Carbs"
                value={carbsGrams}
                idealValue={targets?.carbs}
                percentage={carbsPercent}
                unit="g"
                color={NUTRITION_COLORS.carbs}
                showIdeal={!!targets?.carbs}
              />
              <TargetSummaryRow
                title="Protein"
                value={proteinGrams}
                idealValue={targets?.protein}
                percentage={proteinPercent}
                unit="g"
                color={NUTRITION_COLORS.protein}
                showIdeal={!!targets?.protein}
              />
              <TargetSummaryRow
                title="Fiber"
                value={fiberGrams}
                idealValue={targets?.fiber}
                percentage={fiberPercent}
                unit="g"
                color={NUTRITION_COLORS.fiber}
                showIdeal={!!targets?.fiber}
              />
              <TargetSummaryRow
                title="Fats"
                value={fatGrams}
                idealValue={targets?.fats}
                percentage={fatsPercent}
                unit="g"
                color={NUTRITION_COLORS.fats}
                showIdeal={!!targets?.fats}
              />
            </div>
          </div>
        </div>
      </WidgetContent>
    </Widget>
  );
};
